import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Settings,
    CreditCard,
    Calendar,
    Pause,
    X,
    AlertTriangle,
    Gift,
    CheckCircle,
    Heart,
    Users,
    Shield,
    Clock
} from 'lucide-react';
import { PaymentsService } from '@/services/payments.service';
import { toast } from 'sonner';
import { brand } from '@/config/brand';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface Subscription {
    status: number;
    auto_renew_status: number;
    expires_at: string;
    id: string;
    group_product: {
        name: string;
        description: string;
    };
    subscription_price: number;
}

interface AutoPaymentSettingsModalProps {
    subscriptionId: string;
    isOpen: boolean;
    onClose: () => void;
    subscription?: Subscription;
}

const AutoPaymentSettingsModal: React.FC<AutoPaymentSettingsModalProps> = ({
                                                                               subscriptionId,
                                                                               isOpen,
                                                                               onClose,
                                                                               subscription
                                                                           }) => {
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [discounts, setDiscounts] = useState<Array<{ id: number; discount_percentage: number; months: number }>>([]);
    const [currentDiscountIndex, setCurrentDiscountIndex] = useState(0);
    const [countdown, setCountdown] = useState(0);
    const [formData, setFormData] = useState({
        reason: '',
        feedback: '',
        email: '',
        confirmText: ''
    });

    // Генерируем случайную статистику пользователя
    const [userStats] = useState({
        daysWithUs: Math.floor(Math.random() * 180) + 30,
        dataProtected: (Math.random() * 500 + 100).toFixed(0),
        threatsBlocked: Math.floor(Math.random() * 1000) + 200
    });

    const TOTAL_STEPS = 6;

    React.useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleOptionChange = (value: string) => {
        setSelectedOption(value);
        if (value === 'disable_completely') {
            setCurrentStep(2); // Переходим к retention процессу
        }
    };

    const fetchDiscounts = async () => {
        try {
            setIsLoading(true);
            const data = await PaymentsService.getDiscounts();
            setDiscounts(data);
            setCurrentDiscountIndex(0);
            return data.length > 0;
        } catch (error) {
            console.error('Failed to fetch discounts:', error);
            // Если пользователь уже имел скидку, продолжаем без показа скидок
            // @ts-ignore
            if (error.response?.data?.message?.includes('уже были или есть активная скидка')) {
                return false;
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const applyDiscount = async (id: number) => {
        try {
            setIsLoading(true);
            await PaymentsService.applyDiscount({ discount_id: id });
            toast.success('Скидка успешно применена! Новые цены вступят в силу со следующего платежа.');
            onClose();
            setTimeout(() => window.location.reload(), 1500);
        } catch (error) {
            console.error('Error applying discount:', error);
            toast.error('Ошибка при применении скидки. Попробуйте позже.');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelSubscription = async () => {
        try {
            setIsLoading(true);
            await PaymentsService.cancelSubscription(subscriptionId);
            toast.success('Автоплатеж отключен');
            setTimeout(() => window.location.reload(), 1000);
        } catch (error) {
            toast.error('Ошибка отключения автоплатежа');
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    const handleReasonSelect = async (reason: string) => {
        setFormData({...formData, reason});

        // Всегда проверяем наличие скидок после выбора причины
        const hasDiscounts = await fetchDiscounts();

        if (hasDiscounts) {
            setCurrentStep(4); // Переходим к скидкам
        } else {
            setCurrentStep(3); // Переходим к поддержке
        }
    };

    const handleNextDiscount = () => {
        if (currentDiscountIndex < discounts.length - 1) {
            setCurrentDiscountIndex(currentDiscountIndex + 1);
        } else {
            // Все скидки показаны, переходим к отмене
            setCurrentStep(5);
        }
    };

    const handleFinalCancel = async () => {
        setCountdown(1);
        setTimeout(() => {
            cancelSubscription();
        }, 1000);
    };

    const getMonthsText = (months: number) => {
        if (months === 1) return 'месяц';
        if (months > 1 && months < 5) return 'месяца';
        return 'месяцев';
    };

    const handleDialogReset = () => {
        onClose();
        setCurrentStep(1);
        setSelectedOption('');
        setDiscounts([]);
        setCurrentDiscountIndex(0);
        setFormData({ reason: '', feedback: '', email: '', confirmText: '' });
        setCountdown(0);
    };

    if (currentStep === 1) {
        return (
            <Dialog open={isOpen} onOpenChange={handleDialogReset}>
                <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-white flex items-center gap-2">
                                <Settings className="w-5 h-5" />
                                Настройки автоплатежа
                            </DialogTitle>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleDialogReset}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </DialogHeader>

                    <div className="mt-4 space-y-4">
                        <div className="bg-gray-800/50 rounded-lg p-3">
                            <h4 className="text-white font-medium mb-1">{subscription?.group_product?.name}</h4>
                            <p className="text-gray-400 text-sm">{subscription?.subscription_price} ₽/месяц</p>
                        </div>

                        <div className="space-y-3">
                            <p className="text-gray-300 text-sm">Выберите действие:</p>

                            <RadioGroup value={selectedOption} onValueChange={handleOptionChange}>
                                <label
                                    htmlFor="disable_completely"
                                    className="flex items-center space-x-2 p-3 rounded border border-red-600/30 hover:border-red-500 transition-colors cursor-pointer"
                                >
                                    <RadioGroupItem value="disable_completely" id="disable_completely" />
                                    <div className="flex items-center gap-2 text-red-400">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span className="text-sm">Отключить автоплатеж полностью</span>
                                    </div>
                                </label>
                            </RadioGroup>
                        </div>

                        <Button
                            onClick={() => {
                                if (selectedOption === 'disable_completely') return;
                                toast.info('Функция временно недоступна. Обратитесь в поддержку.');
                            }}
                            disabled={!selectedOption}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                            Применить изменения
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Retention процесс (шаги 2-6)
    const renderRetentionStep = () => {
        switch(currentStep) {
            case 2:
                return (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <Heart className="w-12 h-12 text-red-400 mx-auto mb-2" />
                            <h3 className="text-lg font-semibold text-white mb-2">
                                Мы будем очень скучать! 💔
                            </h3>
                            <p className="text-gray-300 text-sm">
                                Расскажите, что пошло не так?
                            </p>
                        </div>

                        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-4 rounded-lg border border-blue-600/30">
                            <p className="text-blue-300 mb-3">За время с нами вы:</p>
                            <div className="grid grid-cols-3 gap-3 text-center text-sm">
                                <div className="bg-blue-900/30 p-2 rounded">
                                    <div className="text-xl font-bold text-blue-400">{userStats.daysWithUs}</div>
                                    <div className="text-gray-400">дней</div>
                                </div>
                                <div className="bg-green-900/30 p-2 rounded">
                                    <div className="text-xl font-bold text-green-400">{userStats.dataProtected}GB</div>
                                    <div className="text-gray-400">защищено</div>
                                </div>
                                <div className="bg-red-900/30 p-2 rounded">
                                    <div className="text-xl font-bold text-red-400">{userStats.threatsBlocked}</div>
                                    <div className="text-gray-400">угроз</div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {['Слишком дорого', 'Не пользуюсь сервисом', 'Плохое качество', 'Нашел альтернативу', 'Временные финансовые трудности', 'Другое'].map((reason) => (
                                <button
                                    key={reason}
                                    onClick={() => handleReasonSelect(reason)}
                                    className="w-full text-left p-3 rounded border border-gray-600 hover:border-blue-500 hover:bg-gray-800 transition-colors text-white text-sm"
                                    disabled={isLoading}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <Shield className="w-10 h-10 text-green-400 mx-auto mb-2" />
                            <h3 className="text-lg font-semibold text-white">
                                А может попробуем решить проблему?
                            </h3>
                            <p className="text-gray-400 text-sm">
                                Наша поддержка готова помочь персонально
                            </p>
                        </div>

                        <div className="bg-blue-900/30 border border-blue-600/30 p-4 rounded">
                            <ul className="text-blue-200 text-sm space-y-1">
                                <li>• Настройка под ваши задачи</li>
                                <li>• Решение технических проблем</li>
                                <li>• Подбор оптимального сервера</li>
                                <li>• Персональная скидка</li>
                            </ul>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={() => {
                                    toast.success('Заявка отправлена! Мы свяжемся с вами в течение часа');
                                    handleDialogReset();
                                    const encodedQuestion =
                                    	`Здравствуйте, у меня возникли сложности с подпиской на ${brand.metadata.siteName}`
                                    const text = `%0A%0A${encodedQuestion}`
                                    const url = `https://t.me/skufhelp?text=${text}`
                                    window.open(url, '_blank')
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                                Связаться с поддержкой
                            </Button>
                            <Button
                                variant="outline"
                                onClick={async () => {
                                    // Проверяем скидки еще раз, если ранее не проверяли
                                    if (discounts.length === 0) {
                                        const hasDiscounts = await fetchDiscounts();
                                        setCurrentStep(hasDiscounts ? 4 : 5);
                                    } else {
                                        setCurrentStep(5);
                                    }
                                }}
                                className="flex-1 bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                                disabled={isLoading}
                            >
                                Все же отключить
                            </Button>
                        </div>
                    </div>
                );

            case 4:
                const currentDiscount = discounts[currentDiscountIndex];
                if (!currentDiscount) {
                    setCurrentStep(5);
                    return null;
                }

                return (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <Gift className="w-12 h-12 text-purple-400 mx-auto mb-2" />
                            <h3 className="text-xl font-semibold text-white">
                                🎉 Эксклюзивное предложение!
                            </h3>
                            {discounts.length > 1 && (
                                <p className="text-sm text-gray-400 mt-1">
                                    Предложение {currentDiscountIndex + 1} из {discounts.length}
                                </p>
                            )}
                        </div>

                        <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 p-6 rounded-lg border border-purple-500/30">
                            <div className="text-center space-y-3">
                                <div className="text-3xl font-bold text-purple-300">
                                    -{currentDiscount.discount_percentage}%
                                </div>
                                <p className="text-white font-medium text-lg">
                                    Специальная скидка на {currentDiscount.months} {getMonthsText(currentDiscount.months)}
                                </p>
                                <p className="text-sm text-gray-300">
                                    Скидка применится ко всем вашим активным подпискам
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2 text-green-300">
                            <div className="flex items-center">
                                <CheckCircle className="mr-2 h-5 w-5" />
                                <span>Полный доступ ко всем функциям</span>
                            </div>
                            <div className="flex items-center">
                                <CheckCircle className="mr-2 h-5 w-5" />
                                <span>Приоритетная поддержка</span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={() => applyDiscount(currentDiscount.id)}
                                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Применение...' : '🎁 Получить скидку!'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleNextDiscount}
                                className="flex-1 bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                                disabled={isLoading}
                            >
                                Нет, спасибо
                            </Button>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                            <h3 className="text-lg font-semibold text-white">
                                Подтверждение отключения
                            </h3>
                        </div>

                        <div className="bg-red-900/20 border border-red-600/30 p-4 rounded">
                            <p className="text-red-300 font-medium mb-2">⚠️ Что произойдет:</p>
                            <ul className="text-red-200 text-sm space-y-1">
                                <li>• {brand.copy.autopayAccessClosed}</li>
                                <li>• Все настройки удалятся</li>
                                <li>• Статистика сбросится</li>
                            </ul>
                        </div>

                        <Input
                            placeholder="Введите ваш email для подтверждения"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="bg-gray-800 border-gray-600 text-white"
                        />

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep(4)}
                                className="flex-1"
                            >
                                Назад
                            </Button>
                            <Button
                                onClick={() => setCurrentStep(6)}
                                disabled={!formData.email.includes('@')}
                                className="flex-1 bg-orange-600"
                            >
                                Продолжить
                            </Button>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-4">
                        <div className="text-center mb-4">
                            <Clock className="w-10 h-10 text-red-400 mx-auto mb-2" />
                            <h3 className="text-lg font-semibold text-white">
                                Финальное подтверждение
                            </h3>
                        </div>

                        <Input
                            placeholder="Введите 'ОТКЛЮЧИТЬ АВТОПЛАТЕЖ'"
                            value={formData.confirmText}
                            onChange={(e) => setFormData({...formData, confirmText: e.target.value})}
                            className="bg-gray-800 border-gray-600 text-white"
                        />

                        {countdown > 0 && (
                            <div className="text-center">
                                <p className="text-yellow-400">Подождите {countdown} секунд...</p>
                            </div>
                        )}

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep(5)}
                                className="flex-1"
                            >
                                Назад
                            </Button>
                            <Button
                                onClick={handleFinalCancel}
                                disabled={formData.confirmText !== 'ОТКЛЮЧИТЬ АВТОПЛАТЕЖ' || countdown > 0 || isLoading}
                                className="flex-1 bg-red-600"
                            >
                                {isLoading ? 'Отключение...' : 'Подтвердить'}
                            </Button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleDialogReset}>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="text-white flex items-center gap-2">
                            {currentStep === 1 ? (
                                <>
                                    <Settings className="w-5 h-5" />
                                    Настройки автоплатежа
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                                    Отключение автоплатежа
                                    {/*<span className="text-sm text-gray-400 ml-2">*/}
                                    {/*    Шаг {currentStep - 1} из {TOTAL_STEPS - 1}*/}
                                    {/*</span>*/}
                                </>
                            )}
                        </DialogTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleDialogReset}
                            className="text-gray-400 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </DialogHeader>

                <div className="mt-4">
                    {renderRetentionStep()}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AutoPaymentSettingsModal;
